import { formatDate } from '@/lib/utils'
import { EnvelopeOpenIcon } from '@heroicons/react/24/outline'
import { useState } from 'react'

import { Button } from '@droidmap/frontend-ui'
import type { MyOrganisationInvitation } from '@droidmap/organisation-service-contract'

export interface InvitationCardProps {
  invitation: MyOrganisationInvitation
  onAccept: () => void
  onDecline: () => void
  /** Which action on *this* card is in flight, if any. */
  pendingAction?: 'accept' | 'decline'
  /**
   * Another card's action is in flight. Answering two invitations at once is
   * harmless server-side, but accepting navigates away, so keep it to one.
   */
  disabled?: boolean
}

/**
 * One open invitation on the organisations dashboard (#3058): which org, who
 * sent it, when, the groups accepting adds you to (#3079), and Accept /
 * Decline. Group names are rendered as text only — they're admin-chosen.
 * Decline asks for confirmation inline — it can't be undone, and the admin
 * would have to re-invite.
 */
export function InvitationCard({
  invitation,
  onAccept,
  onDecline,
  pendingAction,
  disabled = false,
}: InvitationCardProps) {
  const [confirmingDecline, setConfirmingDecline] = useState(false)
  const busy = disabled || pendingAction !== undefined

  return (
    <article
      aria-label={`Invitation to ${invitation.orgName}`}
      className="border-accent-tint bg-accent-soft flex flex-col gap-4 rounded-lg border p-5 sm:flex-row sm:items-center sm:justify-between"
    >
      <div className="flex min-w-0 items-start gap-3.5">
        <div className="border-accent-tint bg-panel text-accent grid h-10 w-10 shrink-0 place-items-center rounded-full border">
          <EnvelopeOpenIcon className="h-5 w-5" aria-hidden />
        </div>
        <div className="min-w-0">
          <h3 className="text-text truncate text-[15px] font-semibold">
            {invitation.orgName}
          </h3>
          <p className="text-text-dim mt-0.5 text-xs leading-relaxed">
            {invitation.inviterFirstName
              ? `${invitation.inviterFirstName} invited you`
              : 'You were invited'}{' '}
            on {formatDate(invitation.created)}
          </p>
          {invitation.groupNames.length > 0 && (
            <p className="text-text-dim mt-1 text-xs leading-relaxed">
              You’ll be added to{' '}
              <span className="text-text">
                {invitation.groupNames.join(', ')}
              </span>
            </p>
          )}
        </div>
      </div>

      {confirmingDecline ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-text-dim text-xs">
            Decline this invitation?
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setConfirmingDecline(false)}
            disabled={pendingAction === 'decline'}
          >
            Keep
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={onDecline}
            isLoading={pendingAction === 'decline'}
            disabled={busy}
          >
            Decline
          </Button>
        </div>
      ) : (
        <div className="flex shrink-0 gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setConfirmingDecline(true)}
            disabled={busy}
          >
            Decline
          </Button>
          <Button
            size="sm"
            onClick={onAccept}
            isLoading={pendingAction === 'accept'}
            disabled={busy}
          >
            Accept
          </Button>
        </div>
      )}
    </article>
  )
}
